import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  BadgeCheck,
  Star,
  MapPin,
  Clock,
  Share2,
  Heart,
  ShieldCheck,
  ChevronDown,
  ChevronUp,
  Check,
  Navigation,
} from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import Button from "../components/ui/Button";
import PayDialog from "../components/PayDialog";
import ShopMap, { directionsUrl } from "../components/ShopMap";
import SocialLinks, { hasSocialLinks } from "../components/business/SocialLinks";
import { Card, Badge } from "../components/ui/Card";
import { formatDuration, formatPrice, resolveMediaUrl } from "../lib/utils";
import { businessJsonLd, useSeo } from "../lib/seo";
import { useFavorite } from "../lib/favorites";

const DAY_ORDER = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];
const DAY_LABELS = {
  mon: "Monday",
  tue: "Tuesday",
  wed: "Wednesday",
  thu: "Thursday",
  fri: "Friday",
  sat: "Saturday",
  sun: "Sunday",
};

function isOpenNow(openingHours) {
  if (!openingHours) return null;
  const now = new Date();
  const day = DAY_ORDER[(now.getDay() + 6) % 7];
  const todays = openingHours[day];
  if (!Array.isArray(todays) || !todays.length) return false;
  const minutes = now.getHours() * 60 + now.getMinutes();
  return todays.some((slot) => {
    const [sh, sm] = (slot.start || "0:0").split(":").map(Number);
    const [eh, em] = (slot.end || "0:0").split(":").map(Number);
    return minutes >= sh * 60 + sm && minutes <= eh * 60 + em;
  });
}

// How the shop appears in search results and when its link is shared.
function businessSeo(business, notFound) {
  if (notFound) return { title: "Shop not found | SHUWA", noindex: true };
  if (!business) return null;

  const kind = business.category?.name || "Business";
  const place = business.city || "Rwanda";
  const image = resolveMediaUrl(business.venue_photo_urls?.[0] || business.logo_url);
  const about = business.description ? `${business.description.replace(/s+/g, " ").trim()} ` : "";

  return {
    title: `${business.name} — ${kind} in ${place} | Book on SHUWA`,
    description: `${about}Book ${business.name} online: see services, prices and reviews, and pick a time that suits you.`.slice(0, 200),
    canonical: `/${business.slug}`,
    image,
    jsonLd: businessJsonLd(business, image),
  };
}

export default function BusinessProfile() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [business, setBusiness] = useState(null);
  const [selected, setSelected] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [expandedCategories, setExpandedCategories] = useState({});
  const [saved, toggleSaved] = useFavorite(business);
  const [packages, setPackages] = useState([]);
  const [checkout, setCheckout] = useState(null);
  // id → status of what was just bought here ("active" or "pending" when
  // paying at the business).
  const [purchased, setPurchased] = useState({});
  const [memberships, setMemberships] = useState([]);
  const [promotions, setPromotions] = useState([]);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setNotFound(false);
    api
      .get(`/businesses/${slug}`)
      .then(({ data }) => setBusiness(data))
      .catch(() => setNotFound(true));
    api
      .get(`/businesses/${slug}/reviews`)
      .then(({ data }) => setReviews(data.data || data || []))
      .catch(() => {});
    api
      .get(`/businesses/${slug}/packages`)
      .then(({ data }) => setPackages(data))
      .catch(() => {});
    api
      .get(`/businesses/${slug}/memberships`)
      .then(({ data }) => setMemberships(data))
      .catch(() => {});
    api
      .get(`/businesses/${slug}/promotions`)
      .then(({ data }) => setPromotions(data))
      .catch(() => {});
  }, [slug]);

  const discountForService = (serviceId) => {
    const applicable = promotions.filter((p) => p.type !== "last_minute" && p.services?.some((s) => s.id === serviceId));
    if (!applicable.length) return 0;
    return Math.max(...applicable.map((p) => p.discount_percent));
  };

  // Opens the checkout dialog for a package or membership.
  const startPurchase = (purchase) => {
    if (!user) {
      navigate(`/login?next=/${slug}`);
      return;
    }
    setCheckout(purchase);
  };

  const buyPackage = (pkg) =>
    startPurchase({
      title: pkg.name,
      amount: Number(pkg.price),
      url: `/packages/${pkg.id}/purchase`,
      id: pkg.id,
    });

  const buyMembership = (membership) =>
    startPurchase({
      title: membership.name,
      amount: Number(membership.price),
      url: `/memberships/${membership.id}/purchase`,
      id: membership.id,
    });

  // Services the owner marked popular are listed first, in their own group.
  const servicesByCategory = useMemo(() => {
    const services = business?.services || [];
    const popular = services.filter((s) => s.is_popular);
    const grouped = services.reduce((acc, s) => {
      const key = s.category || "Other services";
      acc[key] = acc[key] || [];
      acc[key].push(s);
      return acc;
    }, {});
    return popular.length ? { Popular: popular, ...grouped } : grouped;
  }, [business]);

  const ratingBreakdown = useMemo(() => {
    const counts = [0, 0, 0, 0, 0];
    reviews.forEach((r) => {
      const idx = Math.min(5, Math.max(1, Math.round(r.rating))) - 1;
      counts[idx] += 1;
    });
    const max = Math.max(1, ...counts);
    return counts
      .map((count, i) => ({ stars: i + 1, count, pct: Math.round((count / max) * 100) }))
      .reverse();
  }, [reviews]);

  const reviewPhotos = useMemo(
    () => reviews.flatMap((r) => r.photo_urls || []).slice(0, 12),
    [reviews]
  );

  useSeo(businessSeo(business, notFound));

  if (notFound) {
    return (
      <div className="mx-auto max-w-md px-4 py-20 text-center">
        <h1 className="font-montserrat text-2xl font-bold text-slate-900">Shop not found</h1>
        <p className="mt-2 text-muted-foreground">
          This link may be wrong, or the business isn't taking bookings on SHUWA right now.
        </p>
        <Button className="mt-6" onClick={() => navigate("/search")}>
          Find another business
        </Button>
      </div>
    );
  }

  if (!business) {
    return <div className="mx-auto max-w-5xl px-4 py-16 text-center text-muted-foreground">Loading…</div>;
  }

  const toggleService = (id) => {
    setSelected((prev) => (prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id]));
  };

  const total = business.services
    ?.filter((s) => selected.includes(s.id))
    .reduce((sum, s) => sum + Number(s.price), 0);

  // Guests can pick a time freely; BookingFlow asks them to sign up before
  // they confirm and pay.
  const goToBooking = () => {
    if (!selected.length) return;
    // Found via SHUWA search/home (vs. the shop's own link) — used for Boost.
    navigate(`/${slug}/book?services=${selected.join(",")}`, {
      state: { source: location.state?.source || "direct", searchTerm: location.state?.searchTerm },
    });
  };

  const gallery = [
    ...(business.venue_photo_urls || []),
    ...(business.portfolio_photo_urls || []),
  ].filter(Boolean).map(resolveMediaUrl);
  const heroPhoto = gallery[0] || resolveMediaUrl(business.logo_url);
  const open = isOpenNow(business.opening_hours);
  const today = DAY_ORDER[(new Date().getDay() + 6) % 7];
  const todaysHours = business.opening_hours?.[today];

  return (
    <div className="pb-24">
      {/* Gallery */}
      <div className="mx-auto max-w-6xl px-0 pt-4 sm:px-6">
        <div className="grid grid-cols-4 grid-rows-2 gap-1 overflow-hidden sm:rounded-2xl" style={{ height: "min(340px, 55vw)" }}>
          <div className="col-span-4 row-span-2 sm:col-span-2 sm:row-span-2">
            {heroPhoto ? (
              <img src={heroPhoto} alt={business.name} className="h-full w-full object-cover" />
            ) : (
              <div className="flex h-full w-full items-center justify-center bg-muted text-6xl font-montserrat font-bold text-primary/20">
                {business.name?.[0]}
              </div>
            )}
          </div>
          {gallery.slice(1, 5).map((url, i) => (
            <div key={i} className="hidden overflow-hidden sm:block">
              <img src={url} alt={`${business.name} ${i + 2}`} className="h-full w-full object-cover" />
            </div>
          ))}
          {Array.from({ length: Math.max(0, 4 - Math.max(0, gallery.length - 1)) }).map((_, i) => (
            <div key={`ph-${i}`} className="hidden bg-muted sm:block" />
          ))}
        </div>
      </div>

      <div className="mx-auto grid max-w-6xl grid-cols-1 gap-8 px-4 py-6 sm:px-6 lg:grid-cols-[minmax(0,1fr)_320px]">
        <div>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <div className="flex items-center gap-2">
                <h1 className="font-montserrat text-2xl font-bold text-slate-900">{business.name}</h1>
                {business.is_verified && (
                  <Badge variant="primary" title="SHUWA has checked this provider's identity and any licence their work requires">
                    <BadgeCheck className="h-3.5 w-3.5" /> Verified
                  </Badge>
                )}
                {business.is_promoted && <Badge variant="dark">Promoted</Badge>}
              </div>
              <div className="mt-1 flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
                {business.rating_count > 0 && (
                  <span className="flex items-center gap-1 font-medium text-slate-900">
                    <Star className="h-4 w-4 fill-amber-400 text-amber-400" />
                    {Number(business.rating_avg).toFixed(1)} ({business.rating_count} reviews)
                  </span>
                )}
                <span className="flex items-center gap-1">
                  <MapPin className="h-4 w-4" /> {business.city || business.address}
                </span>
                {business.category?.name && <Badge>{business.category.name}</Badge>}
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigator.share?.({ title: business.name, url: window.location.href })}
              >
                <Share2 className="h-4 w-4" /> Share
              </Button>
              <Button variant="outline" size="sm" onClick={toggleSaved} aria-pressed={saved}>
                <Heart className={`h-4 w-4 ${saved ? "fill-red-500 text-red-500" : ""}`} /> {saved ? "Saved" : "Save"}
              </Button>
            </div>
          </div>

          {business.description && (
            <p className="mt-4 text-sm text-slate-600">{business.description}</p>
          )}

          {/* Services */}
          <div className="mt-8">
            <h2 className="mb-3 font-montserrat text-lg font-bold text-slate-900">Services</h2>
            <div className="space-y-6">
              {Object.entries(servicesByCategory).map(([category, services]) => {
                const isExpanded = expandedCategories[category];
                const visibleServices = isExpanded ? services : services.slice(0, 6);
                return (
                  <div key={category}>
                    <h3 className="mb-2 flex items-center justify-between text-sm font-semibold uppercase tracking-wide text-muted-foreground">
                      {category}
                      <span className="text-xs font-normal normal-case text-muted-foreground">
                        {services.length} service{services.length !== 1 ? "s" : ""}
                      </span>
                    </h3>
                    <div className="divide-y divide-border rounded-2xl border border-border">
                      {visibleServices.map((s) => {
                        const discountPercent = discountForService(s.id);
                        return (
                        <div key={s.id} className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
                          <div className="min-w-0">
                            <p className="font-medium text-slate-900">{s.name}</p>
                            {s.description && (
                              <p className="text-sm text-muted-foreground">{s.description}</p>
                            )}
                            <p className="text-xs text-muted-foreground">{formatDuration(s.duration_minutes)}</p>
                          </div>
                          <div className="flex shrink-0 flex-wrap items-center gap-3">
                            {discountPercent > 0 ? (
                              <div className="text-right">
                                <Badge variant="warning">-{discountPercent}%</Badge>
                                <p className="text-xs text-muted-foreground line-through">{formatPrice(s.price)}</p>
                                <span className="font-semibold text-slate-900">
                                  {formatPrice(s.price * (1 - discountPercent / 100))}
                                </span>
                              </div>
                            ) : (
                              <span className="font-semibold text-slate-900">{formatPrice(s.price)}</span>
                            )}
                            <Button
                              size="sm"
                              variant={selected.includes(s.id) ? "secondary" : "primary"}
                              onClick={() => toggleService(s.id)}
                            >
                              {selected.includes(s.id) ? "Selected" : "Book"}
                            </Button>
                          </div>
                        </div>
                        );
                      })}
                    </div>
                    {services.length > 6 && (
                      <button
                        className="mt-2 flex items-center gap-1 text-sm font-medium text-primary"
                        onClick={() =>
                          setExpandedCategories((prev) => ({ ...prev, [category]: !isExpanded }))
                        }
                      >
                        {isExpanded ? (
                          <>
                            Show less <ChevronUp className="h-4 w-4" />
                          </>
                        ) : (
                          <>
                            See all {services.length} services <ChevronDown className="h-4 w-4" />
                          </>
                        )}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
            {!business.services?.length && (
              <p className="text-sm text-muted-foreground">This business hasn't listed services yet.</p>
            )}
          </div>

          {packages.length > 0 && (
            <div className="mt-8">
              <h2 className="mb-3 font-montserrat text-lg font-bold text-slate-900">Packages</h2>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {packages.map((p) => (
                  <Card key={p.id} className="p-4">
                    <p className="font-medium text-slate-900">{p.name}</p>
                    <p className="text-xs text-muted-foreground">
                      {p.session_count} × {p.service?.name}
                    </p>
                    {p.description && <p className="mt-1 text-sm text-slate-600">{p.description}</p>}
                    <div className="mt-3 flex items-center justify-between">
                      <span className="font-semibold text-slate-900">{formatPrice(p.price)}</span>
                      {purchased[p.id] ? (
                        <Badge variant="accent">
                          <Check className="h-3 w-3" /> {purchased[p.id] === "pending" ? "Pay at store" : "Purchased"}
                        </Badge>
                      ) : (
                        <Button size="sm" onClick={() => buyPackage(p)}>
                          Buy package
                        </Button>
                      )}
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {memberships.length > 0 && (
            <div className="mt-8">
              <h2 className="mb-3 font-montserrat text-lg font-bold text-slate-900">Memberships</h2>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {memberships.map((m) => (
                  <Card key={m.id} className="p-4">
                    <p className="font-medium text-slate-900">{m.name}</p>
                    <p className="text-xs text-muted-foreground">
                      Unlimited {m.services?.map((s) => s.name).join(", ")}
                    </p>
                    {m.description && <p className="mt-1 text-sm text-slate-600">{m.description}</p>}
                    <div className="mt-3 flex items-center justify-between">
                      <span className="font-semibold text-slate-900">
                        {formatPrice(m.price)} <span className="text-xs font-normal text-muted-foreground">/ {m.billing_period_days} days</span>
                      </span>
                      {purchased[m.id] ? (
                        <Badge variant="accent">
                          <Check className="h-3 w-3" /> {purchased[m.id] === "pending" ? "Pay at store" : "Purchased"}
                        </Badge>
                      ) : (
                        <Button size="sm" onClick={() => buyMembership(m)}>
                          Join
                        </Button>
                      )}
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {business.staff?.length > 1 && (
            <div className="mt-10">
              <h2 className="mb-3 font-montserrat text-lg font-bold text-slate-900">Meet the team</h2>
              <div className="flex flex-wrap gap-4">
                {business.staff.map((s) => (
                  <div key={s.id} className="w-28 text-center">
                    <div className="mx-auto mb-2 h-16 w-16 overflow-hidden rounded-full bg-muted">
                      {s.avatar_url && <img src={s.avatar_url} alt={s.name} className="h-full w-full object-cover" />}
                    </div>
                    <p className="text-sm font-medium text-slate-900">{s.name}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {(business.safety_rules?.length > 0 || business.amenities?.length > 0) && (
            <div className="mt-10 grid grid-cols-1 gap-8 sm:grid-cols-2">
              {business.safety_rules?.length > 0 && (
                <div>
                  <h2 className="mb-3 font-montserrat text-lg font-bold text-slate-900">
                    Venue Health and Safety Rules
                  </h2>
                  <ul className="space-y-2">
                    {business.safety_rules.map((rule) => (
                      <li key={rule} className="flex items-start gap-2 text-sm text-slate-700">
                        <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-primary" /> {rule}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {business.amenities?.length > 0 && (
                <div>
                  <h2 className="mb-3 font-montserrat text-lg font-bold text-slate-900">Amenities</h2>
                  <ul className="space-y-2">
                    {business.amenities.map((a) => (
                      <li key={a} className="flex items-start gap-2 text-sm text-slate-700">
                        <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" /> {a}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {gallery.length > 0 && (
            <div className="mt-10">
              <h2 className="mb-3 font-montserrat text-lg font-bold text-slate-900">Photos</h2>
              <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
                {gallery.map((url, i) => (
                  <div key={i} className="aspect-square overflow-hidden rounded-xl bg-muted">
                    <img src={url} alt={`${business.name} ${i + 1}`} className="h-full w-full object-cover" />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Reviews */}
          <div className="mt-10">
            <h2 className="mb-3 font-montserrat text-lg font-bold text-slate-900">Reviews</h2>

            {business.rating_count > 0 && (
              <div className="mb-6 flex flex-col gap-6 sm:flex-row sm:items-center">
                <div className="text-center">
                  <p className="font-montserrat text-4xl font-bold text-slate-900">
                    {Number(business.rating_avg).toFixed(1)}
                  </p>
                  <div className="mt-1 flex items-center justify-center gap-0.5">
                    {Array.from({ length: 5 }).map((_, i) => (
                      <Star
                        key={i}
                        className={`h-4 w-4 ${
                          i < Math.round(business.rating_avg)
                            ? "fill-amber-400 text-amber-400"
                            : "text-muted-foreground/30"
                        }`}
                      />
                    ))}
                  </div>
                  <p className="mt-1 text-xs text-muted-foreground">{business.rating_count} reviews</p>
                </div>
                <div className="flex-1 space-y-1.5">
                  {ratingBreakdown.map((row) => (
                    <div key={row.stars} className="flex items-center gap-2 text-xs text-muted-foreground">
                      <span className="w-3">{row.stars}</span>
                      <div className="h-2 flex-1 overflow-hidden rounded-full bg-muted">
                        <div className="h-full rounded-full bg-amber-400" style={{ width: `${row.pct}%` }} />
                      </div>
                      <span className="w-6 text-right">{row.count}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {reviewPhotos.length > 0 && (
              <div className="mb-6">
                <p className="mb-2 text-sm font-semibold text-slate-900">Client photos</p>
                <div className="flex gap-2 overflow-x-auto pb-2">
                  {reviewPhotos.map((url, i) => (
                    <div key={i} className="h-20 w-20 shrink-0 overflow-hidden rounded-xl bg-muted">
                      <img src={url} alt={`Client ${i + 1}`} className="h-full w-full object-cover" />
                    </div>
                  ))}
                </div>
              </div>
            )}

            {reviews.length ? (
              <div className="space-y-4">
                {reviews.map((r) => (
                  <div key={r.id} className="border-b border-border pb-4 last:border-0">
                    <div className="flex items-center gap-3">
                      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-muted text-sm font-semibold text-slate-600">
                        {(r.customer?.full_name || "V")[0]}
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-semibold text-slate-900">
                          {r.customer?.full_name || "Verified customer"}
                        </p>
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          <span className="flex items-center gap-0.5">
                            {Array.from({ length: 5 }).map((_, i) => (
                              <Star
                                key={i}
                                className={`h-3 w-3 ${
                                  i < r.rating ? "fill-amber-400 text-amber-400" : "text-muted-foreground/30"
                                }`}
                              />
                            ))}
                          </span>
                          {r.created_at && <span>{new Date(r.created_at).toLocaleDateString()}</span>}
                        </div>
                      </div>
                    </div>
                    {r.comment && <p className="mt-2 text-sm text-slate-600">{r.comment}</p>}
                    {r.business_reply && (
                      <div className="mt-3 ml-12 rounded-xl bg-muted p-3">
                        <p className="text-xs font-semibold text-slate-900">Response from the business</p>
                        <p className="mt-1 text-sm text-slate-600">{r.business_reply}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">No reviews yet.</p>
            )}
          </div>
        </div>

        {/* Sidebar */}
        <aside className="space-y-4 lg:sticky lg:top-20 lg:self-start">
          <Card className="overflow-hidden p-0">
            {(business.latitude && business.longitude) ? (
              <div className="h-48 w-full bg-muted">
                <ShopMap latitude={business.latitude} longitude={business.longitude} />
              </div>
            ) : (
              <div className="flex h-36 w-full items-center justify-center bg-muted text-sm text-muted-foreground">
                <MapPin className="mr-1 h-4 w-4" /> No map available
              </div>
            )}
            <div className="space-y-3 p-4">
              <div className="flex items-start justify-between gap-3">
                <p className="text-sm text-slate-600">
                  {[business.address, business.city].filter(Boolean).join(", ")}
                </p>
                {business.latitude && business.longitude && (
                  <a
                    href={directionsUrl(business.latitude, business.longitude)}
                    target="_blank"
                    rel="noreferrer"
                    className="flex shrink-0 items-center gap-1 text-sm font-semibold text-primary hover:underline"
                  >
                    <Navigation className="h-4 w-4" /> Directions
                  </a>
                )}
              </div>

              <div className="flex items-center justify-between border-t border-border pt-3">
                <span className="flex items-center gap-2 text-sm font-semibold text-slate-900">
                  <Clock className="h-4 w-4" />
                  {open === null ? "Hours" : open ? "Open now" : "Closed now"}
                </span>
                <span className="text-xs text-muted-foreground">
                  {Array.isArray(todaysHours) && todaysHours.length
                    ? todaysHours.map((h) => `${h.start}–${h.end}`).join(", ")
                    : "Closed today"}
                </span>
              </div>

              {business.opening_hours && (
                <ul className="space-y-1 text-sm text-muted-foreground">
                  {DAY_ORDER.filter((d) => business.opening_hours[d] !== undefined).map((day) => (
                    <li key={day} className="flex justify-between">
                      <span className={day === today ? "font-semibold text-slate-900" : ""}>
                        {DAY_LABELS[day]}
                      </span>
                      <span className={day === today ? "font-semibold text-slate-900" : ""}>
                        {Array.isArray(business.opening_hours[day]) && business.opening_hours[day].length
                          ? business.opening_hours[day].map((h) => `${h.start}–${h.end}`).join(", ")
                          : "Closed"}
                      </span>
                    </li>
                  ))}
                </ul>
              )}

              <Button className="w-full" onClick={goToBooking} disabled={!selected.length}>
                Book now
              </Button>
            </div>
          </Card>

          {(business.phone || hasSocialLinks(business.social_links)) && (
            <Card className="p-4">
              <p className="text-sm font-semibold text-slate-900">Contact</p>
              {business.phone && <p className="mt-1 text-sm text-muted-foreground">{business.phone}</p>}
              <SocialLinks links={business.social_links} className="mt-3" />
            </Card>
          )}
        </aside>
      </div>

      {selected.length > 0 && (
        <div className="fixed inset-x-0 bottom-0 z-30 border-t border-border bg-white/95 backdrop-blur">
          <div className="mx-auto flex max-w-6xl items-center justify-between px-4 py-3 sm:px-6">
            <div>
              <p className="text-sm text-muted-foreground">{selected.length} service(s) selected</p>
              <p className="font-semibold text-slate-900">{formatPrice(total)}</p>
            </div>
            <Button onClick={goToBooking}>Next</Button>
          </div>
        </div>
      )}

      {checkout && (
        <PayDialog
          title={checkout.title}
          amount={checkout.amount}
          url={checkout.url}
          onClose={() => setCheckout(null)}
          onPaid={(data) => {
            setPurchased((prev) => ({ ...prev, [checkout.id]: data?.status }));
            setCheckout(null);
          }}
        />
      )}
    </div>
  );
}
